import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  Col,
  Row,
  NMForm,
  Button,
  usePub,
  OutlinedButton,
} from "@neumetric/blueprint-lib/components";
import { useDispatch, useSelector } from "react-redux";
import { getCurrentThreatType, mitigatorActions } from "../../../store/";

const getThreatTypeFields = (isViewMode, isEditMode) => {
  const fields = [];
  if (isViewMode || isEditMode) {
    fields.push({
      label: "Threat Type ID",
      labelAlign: "top",
      attribute: "threat_type_id",
      placeholder: "",
      readOnly: true,
      typeRepresentation: "TEXT",
      classes: { root: "w-100", label: "text-right w-110-px", input: "pl-12" },
    });
  }
  fields.push({
    label: "Threat Type",
    labelAlign: "top",
    attribute: "type",
    placeholder: "Threat",
    typeRepresentation: "TEXT",
    classes: { root: "w-100", label: "text-right w-110-px", input: "pl-12" },
  });
  return fields;
};

export const CreateNewThreatType = () => {
  const dispatch = useDispatch();
  const publish = usePub();
  const [submitting, setSubmitting] = useState(false);
  const _ref = useRef(null);
  const viewRef = useRef(null);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const currentTab = params.get("tab");
  const threatTypeId = params.get("id");
  const currentThreatType = useSelector(getCurrentThreatType);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (currentThreatType && viewRef.current) {
      viewRef.current.updateFormData({
        threat_type_id: currentThreatType.threat_type_id,
        type: currentThreatType.threat_type,
      });
    }
  }, [currentThreatType]);
  useEffect(() => {
    if (!threatTypeId) return;
    dispatch(
      mitigatorActions.fetchThreatType({ threat_type_id: threatTypeId }),
    );
  }, [threatTypeId]);

  const onComplete = () => {
    setSubmitting(false);
    setIsEditMode(false); // return to view mode
    publish("MITIGATOR_HIDE_NEW_CONTENT");
  };
  const onFailure = () => {
    setSubmitting(false);
  };
  const handleSave = () => {
    if (!_ref.current.validate()) {
      return;
    }
    const values = _ref.current.getValues();
    const body = {
      threat_type_id: values.threat_type_id || "",
      threat_type: values.type,
      status: values.status || "draft",
    };
    const action = isEditMode
      ? mitigatorActions.updateThreatType({
        id: threatTypeId,
        status: currentThreatType?.status,
        ...body,
      })
      : mitigatorActions.createThreatType(body);
    action.onComplete = onComplete;
    action.onFailure = onFailure;
    setSubmitting(true);
    dispatch(action);
  };
  const handleCancel = () => {
    if (submitting) return;
    _ref.current?.reset?.();
    publish("MITIGATOR_HIDE_NEW_CONTENT"); //hides create panel
  };
  return (
    <div
      style={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "auto",
        padding: "0 12px",
        boxSizing: "border-box",
      }}
    >
      {threatTypeId && currentThreatType && !isEditMode ? (
        <Col className="w-100 h-100 overflow-hide mt-36 pb-16">
          <Row justify="between" className="w-100 pl-16 pr-16">
            <span className="ff-exo2 fw-500 f22 line-28  mb2 c238787">
              Threat Type
            </span>
            <div className="pr-10">
              <Button
                label="Close"
                icon="CANCEL"
                onClick={() => publish("MITIGATOR_HIDE_NEW_CONTENT")}
              />
            </div>
          </Row>
          <Col className="pl-30 pr-36 w-100">
            {/* VIEW MODE FORM */}
            <NMForm
              className="w-100"
              ref={viewRef}
              readOnly={true}
              fields={getThreatTypeFields(true, false)}
              formData={{
                threat_type_id: currentThreatType.threat_type_id,
                type: currentThreatType.threat_type,
              }}
            />
            <Row
              className="mt-16 w-100"
              justify="end"
              align="center"
              style={{ gap: "12px" }}
            >
              {currentTab === "draft" && (
                <>
                  <Button
                    label="Edit"
                    icon="EDIT"
                    onClick={() => setIsEditMode(true)}
                  />
                  <Button
                    label="Activate"
                    icon="CHECK_CONFIRM"
                    onClick={() => {
                      dispatch(
                        mitigatorActions.updateThreatType({
                          id: threatTypeId,
                          threat_type: currentThreatType.type,
                          status: "active",
                        }),
                      );
                      publish("MITIGATOR_HIDE_NEW_CONTENT");
                    }}
                  />
                </>
              )}
              {currentTab === "active" && (
                <>
                  <Button
                    label="Archive"
                    icon="OPEN_FOLDER"
                    variant="CAUTION"
                    onClick={() => {
                      dispatch(
                        mitigatorActions.updateThreatType({
                          id: threatTypeId,
                          threat_type: currentThreatType.type,
                          status: "archive",
                        }),
                      );
                      publish("MITIGATOR_HIDE_NEW_CONTENT");
                    }}
                  />
                </>
              )}
              {currentTab === "archive" && (
                <>
                  <Button
                    label="Unarchive"
                    icon="OPEN_FOLDER"
                    onClick={() => {
                      dispatch(
                        mitigatorActions.updateThreatType({
                          id: threatTypeId,
                          threat_type: currentThreatType.type,
                          status: "draft",
                        }),
                      );
                      publish("MITIGATOR_HIDE_NEW_CONTENT");
                    }}
                  />
                </>
              )}
            </Row>
          </Col>
        </Col>
      ) : (
        <>
          <Col className="w-100 h-100 overflow-hide mt-36 pb-16">
            <Row justify="between" align="center" className="w-100 pl-16 pr-16">
              <span className="ff-exo2 fw-500 f22 line-28  mb2 c238787">
                Threat Type
              </span>
            </Row>
            <NMForm
              className="w-100 pl-30 pr-36"
              ref={_ref}
              formData={
                isEditMode
                  ? {
                    threat_type_id: currentThreatType?.threat_type_id ?? "",
                    type: currentThreatType?.threat_type ?? "",
                  }
                  : {
                    threat_type_id: "",
                    type: "",
                  }
              }
              id="new-threat-type-form"
              fields={
                isEditMode
                  ? getThreatTypeFields(false, true)
                  : getThreatTypeFields(false, false)
              }
            />
            <Row
              className="pr-26 mt-16 w-100"
              justify="end"
              align="center"
              style={{ gap: "12px" }}
            >
              {" "}
              <OutlinedButton
                label="Cancel"
                icon="CANCEL"
                onClick={handleCancel}
                disabled={submitting}
              />
              <Button
                label="Save"
                icon="SAVE"
                onClick={handleSave}
                loading={submitting}
              />
            </Row>
          </Col>
        </>
      )}
    </div>
  );
};
